import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { REJECTION_REASONS, REVISABLE, fmtDate } from "@/lib/meta";
import { useStages } from "@/lib/stages";
import { StageBadge } from "@/components/StageBadge";
import Timeline from "@/components/Timeline";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { History, Building2, MapPin, UserRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function DealDrawer({ dealId, open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const { stages: STAGES, labels: STAGE_LABEL } = useStages();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");
  const [noteType, setNoteType] = useState("note");
  const [tab, setTab] = useState("pipeline");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectCode, setRejectCode] = useState("");
  const [rejectNote, setRejectNote] = useState("");

  const load = () => {
    if (!dealId) return;
    setLoading(true);
    api.get(`/deals/${dealId}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
  };
  useEffect(() => { if (open && dealId) load(); }, [open, dealId]);
  useEffect(() => {
    if (open && user?.role === "admin") api.get("/users").then(({ data }) => setUsers(data.filter((u) => u.active))).catch(() => {});
  }, [open, user]);

  const deal = data?.deal;
  const closed = deal?.status !== "open";

  const changeStage = async (stage) => {
    try { await api.put(`/deals/${dealId}/stage`, { stage }); load(); onChanged?.(); toast.success(`Moved to ${STAGE_LABEL[stage]}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const reviseField = async (field, value) => {
    try { await api.put(`/deals/${dealId}/field`, { field, value }); load(); toast.success("Updated"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const patch = async (body) => {
    try { await api.put(`/deals/${dealId}`, body); load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const assignRep = async (rep) => {
    try { await api.post(`/deals/${dealId}/assign`, { rep }); load(); onChanged?.(); toast.success("Deal assigned & rep notified"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const addEvent = async () => {
    if (!note.trim()) return;
    try { await api.post(`/deals/${dealId}/events`, { type: noteType, note }); setNote(""); load(); toast.success("Logged"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const doReject = async () => {
    if (!rejectCode) return;
    try { await api.post(`/deals/${dealId}/reject`, { code: rejectCode, note: rejectNote }); setRejectOpen(false); setRejectCode(""); setRejectNote(""); load(); onChanged?.(); toast.success("Deal rejected"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[720px] p-0 overflow-y-auto bg-white" data-testid="deal-drawer">
        {loading || !deal ? (
          <div className="h-full flex items-center justify-center py-20">
            <SheetTitle className="sr-only">Deal</SheetTitle>
            <Loader2 className="h-6 w-6 animate-spin text-[#0047FF]" />
          </div>
        ) : (
          <div>
            <SheetHeader className="px-6 py-5 border-b border-gray-200">
              <SheetTitle className="font-heading text-xl flex items-center gap-2">
                <Building2 className="h-5 w-5 text-[#0047FF]" /> {deal.property_name}
              </SheetTitle>
              <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{deal.property_city || "—"}</span>
                <span className="flex items-center gap-1"><UserRound className="h-3.5 w-3.5" />{deal.rep_name || "Unassigned"}</span>
                <StageBadge stage={deal.stage} />
                {closed && <span className="text-xs font-semibold text-rose-600 uppercase">{deal.status}</span>}
              </div>
              {data.siblings?.length > 0 && (
                <div className="mt-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1" data-testid="shared-property-note">
                  Shared property — {data.siblings.length} other deal(s) exist, incl. {data.siblings[0].rep_name || "another rep"} at {STAGE_LABEL[data.siblings[0].stage]}.
                </div>
              )}
            </SheetHeader>

            <Tabs value={tab} onValueChange={setTab} className="px-6 py-4">
              <TabsList className="bg-transparent border-b border-gray-200 rounded-none h-auto p-0 gap-4 w-full justify-start">
                <TabsTrigger value="pipeline" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0047FF] data-[state=active]:bg-transparent px-1 pb-2" data-testid="tab-pipeline">Pipeline & Fields</TabsTrigger>
                <TabsTrigger value="timeline" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0047FF] data-[state=active]:bg-transparent px-1 pb-2" data-testid="tab-timeline">Timeline ({data.events.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="pipeline" className="pt-5 space-y-6">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Stage</Label>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {STAGES.map((s) => (
                      <button key={s.key} disabled={closed} onClick={() => changeStage(s.key)} data-testid={`set-stage-${s.key}`}
                        className={`text-xs px-2.5 py-1 rounded border transition-colors ${deal.stage === s.key ? "bg-[#0047FF] text-white border-[#0047FF]" : "bg-white text-gray-600 border-gray-200 hover:border-[#0047FF] disabled:opacity-40"}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Model</Label>
                    <Select value={deal.model || ""} onValueChange={(v) => patch({ model: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="deal-model-select"><SelectValue placeholder="Select model" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="FOCO">FOCO</SelectItem>
                        <SelectItem value="COCO">COCO</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {user?.role === "admin" && (
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Assigned rep</Label>
                      <Select value={deal.rep || ""} onValueChange={assignRep} disabled={closed}>
                        <SelectTrigger className="mt-1.5" data-testid="assign-rep-select"><SelectValue placeholder="Assign a rep" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Revisable fields (version-tracked)</Label>
                  {REVISABLE.map((f) => (
                    <RevisableRow key={f.key} field={f} value={deal[f.key]} history={deal.field_history?.[f.key] || []} onSave={(v) => reviseField(f.key, v)} disabled={closed} />
                  ))}
                </div>

                {!closed && (
                  <Button variant="destructive" onClick={() => setRejectOpen(true)} data-testid="reject-deal-btn">Reject deal…</Button>
                )}
                {closed && deal.rejection_reason && (
                  <div className="text-sm bg-rose-50 border border-rose-200 text-rose-800 rounded px-3 py-2">
                    Rejected: {(deal.rejection_reason.code || "").replace(/_/g, " ")}{deal.rejection_reason.note ? ` — ${deal.rejection_reason.note}` : ""}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="timeline" className="pt-5">
                {!closed && (
                  <div className="mb-5 border border-gray-200 rounded-md p-3 space-y-2">
                    <div className="flex gap-2">
                      <Select value={noteType} onValueChange={setNoteType}>
                        <SelectTrigger className="w-36" data-testid="event-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="note">Note</SelectItem>
                          <SelectItem value="call">Call</SelectItem>
                          <SelectItem value="site_visit">Site Visit</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note, call summary or site-visit outcome…" rows={2} data-testid="event-note-input" />
                    <Button size="sm" onClick={async () => { await addEvent(); setTab("timeline"); }} data-testid="add-event-btn">Log activity</Button>
                  </div>
                )}
                <Timeline events={data.events} />
              </TabsContent>
            </Tabs>
          </div>
        )}

        <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
          <DialogContent className="bg-white" data-testid="reject-dialog">
            <DialogHeader><DialogTitle>Reject deal</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Reason</Label>
                <Select value={rejectCode} onValueChange={setRejectCode}>
                  <SelectTrigger className="mt-1.5" data-testid="reject-reason-select"><SelectValue placeholder="Select a reason…" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {REJECTION_REASONS.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Textarea value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} className="mt-1.5" data-testid="reject-note-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={doReject} disabled={!rejectCode} data-testid="confirm-reject-btn">Confirm rejection</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}

function RevisableRow({ field, value, history, onSave, disabled }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  useEffect(() => setVal(value || ""), [value]);
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1">
        <div className="text-xs text-gray-500">{field.label}</div>
        {editing ? (
          <div className="flex gap-2 mt-1">
            <Input value={val} onChange={(e) => setVal(e.target.value)} className="h-8" data-testid={`field-input-${field.key}`} />
            <Button size="sm" className="h-8" onClick={() => { onSave(val); setEditing(false); }} data-testid={`field-save-${field.key}`}>Save</Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        ) : (
          <div className="text-sm font-medium text-gray-900">{value || <span className="text-gray-400">Not set</span>}</div>
        )}
      </div>
      {!editing && <Button size="sm" variant="outline" className="h-8" disabled={disabled} onClick={() => setEditing(true)} data-testid={`field-edit-${field.key}`}>Edit</Button>}
      {history.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <Button size="sm" variant="ghost" className="h-8 px-2" data-testid={`field-history-${field.key}`}><History className="h-4 w-4" /></Button>
          </PopoverTrigger>
          <PopoverContent className="bg-white w-72" data-testid={`field-history-popover-${field.key}`}>
            <div className="text-xs font-semibold mb-2">History</div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {[...history].reverse().map((h, i) => (
                <div key={i} className="text-xs border-b border-gray-100 pb-1.5">
                  <div className="font-medium text-gray-900">{h.value}</div>
                  <div className="text-gray-400">{h.actor_name} · {fmtDate(h.at)}</div>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
